import { useEffect, useRef, useState } from 'react';
import { resolveBrandLogoUrl, brandInitials, invalidateBrandLogoCache } from '@/lib/brand-logo';
import { cn } from '@/lib/utils';
import { Package } from 'lucide-react';

type Props = {
  name?: string | null;
  logoUrl?: string | null;
  size?: number;
  className?: string;
  rounded?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  alt?: string;
  /**
   * Fallback style when the logo is missing or fails to load.
   *  - 'initials' (default) → deterministic pastel avatar with brand initials
   *  - 'flowtokos'          → neutral FlowTokOS default brand icon (documents/PDFs)
   */
  fallback?: 'initials' | 'flowtokos';
  fit?: 'cover' | 'contain';
};

const bgSeed = (name?: string | null) => {
  let h = 0;
  for (const c of (name || 'B')) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  const hue = h % 360;
  // Slightly different palette from AgencyLogo so brands read as products, not orgs.
  return `hsl(${hue}, 55%, 40%)`;
};

const RADIUS_MAP: Record<NonNullable<Props['rounded']>, string> = {
  sm: '4px',
  md: '6px',
  lg: '8px',
  xl: '12px',
  '2xl': '16px',
  full: '9999px',
};

/**
 * Robust brand-logo renderer used everywhere in the platform.
 * - Resolves storage URLs (brand-logos bucket) to short-lived signed URLs.
 * - Passes through external URLs (some brands still use hosted CDN links).
 * - Self-heals stale signed URLs by force-refreshing once before giving up.
 * - Falls back to a deterministic initials avatar (or FlowTokOS product icon)
 *   on any load failure — never leaks a broken image icon.
 */
export function BrandLogo({
  name,
  logoUrl,
  size = 32,
  className,
  rounded = 'lg',
  alt,
  fallback = 'initials',
  fit = 'cover',
}: Props) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const retriedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    setLoaded(false);
    setSrc(null);
    retriedRef.current = false;
    if (!logoUrl) return;
    resolveBrandLogoUrl(logoUrl).then((url) => {
      if (!cancelled) setSrc(url);
    });
    return () => { cancelled = true; };
  }, [logoUrl]);

  const dims = { width: size, height: size };
  const radius = RADIUS_MAP[rounded] ?? RADIUS_MAP.lg;

  const handleError = () => {
    if (!retriedRef.current) {
      // Self-heal: signed URL may have expired mid-flight — bust cache and mint a new one once.
      retriedRef.current = true;
      invalidateBrandLogoCache(logoUrl);
      resolveBrandLogoUrl(logoUrl, { forceRefresh: true }).then((url) => {
        if (url && url !== src) setSrc(url);
        else setFailed(true);
      }).catch(() => setFailed(true));
      return;
    }
    // eslint-disable-next-line no-console
    console.warn('[brand-logo] failed to load', { name, logoUrl, resolved: src });
    setFailed(true);
  };

  // Successful image render — with subtle loading skeleton behind while decoding.
  if (src && !failed) {
    return (
      <div
        style={{ ...dims, borderRadius: radius }}
        className={cn('relative flex-shrink-0 overflow-hidden bg-muted', className)}
      >
        {!loaded && (
          <div
            className="absolute inset-0 animate-pulse bg-muted"
            aria-hidden
          />
        )}
        <img
          src={src}
          alt={alt ?? (name ? `${name} logo` : '')}
          loading="lazy"
          decoding="async"
          style={{ ...dims, borderRadius: radius }}
          className={cn(
            fit === 'contain' ? 'object-contain' : 'object-cover',
            'block w-full h-full transition-opacity duration-150',
            loaded ? 'opacity-100' : 'opacity-0',
          )}
          onLoad={() => setLoaded(true)}
          onError={handleError}
        />
      </div>
    );
  }

  if (fallback === 'flowtokos') {
    return (
      <div
        style={{ ...dims, borderRadius: radius }}
        className={cn(
          'flex items-center justify-center bg-muted text-muted-foreground flex-shrink-0 select-none border border-border',
          className,
        )}
        aria-label={alt ?? (name ? `${name} logo` : 'Brand')}
        role="img"
      >
        <Package style={{ width: size * 0.5, height: size * 0.5 }} />
      </div>
    );
  }

  return (
    <div
      style={{
        ...dims,
        borderRadius: radius,
        background: bgSeed(name),
        fontSize: Math.max(9, Math.round(size * 0.38)),
      }}
      className={cn(
        'flex items-center justify-center text-white font-semibold flex-shrink-0 select-none',
        className,
      )}
      aria-label={alt ?? (name ? `${name} logo` : 'Brand')}
      role="img"
    >
      {brandInitials(name)}
    </div>
  );
}
